import * as Enumerable from 'linq';
import { catchError, lastValueFrom, map, Observable, of, Subject } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { Allocation, AllocationCard, AllocationStatus, ItemDeletedResponse, Source } from '@jms/model';
import { AllocationEventData } from '@jms/shared/module.scheduling.allocations.pro';
import { AllocationUtility } from '@jms/shared/module.scheduling.allocations.pro/src/helpers';

import { AppointmentContext, AppointmentParams } from '../models';
import { AppointmentsService } from './appointments.service.interface';

export abstract class AppointmentServiceBase implements AppointmentsService {

    protected readonly _error$ = new Subject<any>();
    public readonly error$ = this._error$.asObservable();

    public abstract source: Source;
    public abstract api: string;
    public http = inject(HttpClient);

    constructor() { }

    abstract getAll(params: AppointmentParams): Observable<Allocation[]>;

    abstract create(allocation: Allocation, context: AppointmentContext): Observable<Allocation[]>;


    get(id: string): Observable<Allocation> {

        return this.http.get<Allocation>(`${this.api}/details/${id}`)
            .pipe(
                map(res => res),
                catchError(() => of(null))
            );

    }


    delete(appointmentId: string): Observable<ItemDeletedResponse> {

        return this.http.delete<ItemDeletedResponse>(`${this.api}/${appointmentId}`)
            .pipe(
                map(res => res),
                catchError(() => of(null))
            );
    }


    update(allocation: Partial<Allocation>): Observable<Allocation> {

        return this.http.patch<Allocation>(`${this.api}`, allocation)
            .pipe(
                map(res => res),
                catchError((error) => {
                    this._error$.next(error?.error?.message ?? "An unknown error occurred")
                    return of(null);
                })
            );
    }


    getForPeriod(start: Date, end: Date, selected?: Allocation): Promise<AllocationEventData[]> {

        const queryParams = {
            start: start.toISOString(),
            end: end.toISOString()
        };

        const data = this.http.get<AllocationCard[]>(`${this.api}/meta`, { params: queryParams })
            .pipe(
                map(allocations =>
                    Enumerable.from(allocations)
                        .select(c => {

                            const isSelected = c._id === selected?._id

                            const editable = isSelected && !(AllocationUtility.isShadow(c) || c.locked || c.status === AllocationStatus.Completed);

                            const data: AllocationEventData = {
                                id: c._id,
                                title: c.status === AllocationStatus.Overhead
                                    ? c?.overhead?.name
                                    : c?.workOrder?.name,
                                start: c.startDate,
                                end: c.endDate,
                                resourceId: c?.resource?._id,
                                resourceEditable: editable,
                                editable: editable,
                                selected: isSelected,
                                entity: c
                            };

                            return data;
                        })
                        .toArray()
                ),
                catchError(error => {
                    console.log(error);
                    return of([]);
                })
            );

        return lastValueFrom(data);
    }

    canAdd(): boolean {
        return true;
    }

    canEdit(item: Allocation): boolean {
        return true;
    }


    canDelete(item: Allocation): boolean {
        return true;
    }

}
